import { ref, onMounted, onUnmounted } from "vue";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';

export default function usePWAInstall() {
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const canInstall = ref(false);
  const isInstalled = ref(false);
  const platform = ref<Platform>('unknown');

  // Detecta a plataforma do usuário
  const detectPlatform = (): Platform => {
    const userAgent = window.navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }

    if (/android/.test(userAgent)) {
      return 'android';
    }

    if (/win|mac|linux/.test(userAgent)) {
      return 'desktop';
    }

    return 'unknown';
  };

  // Verifica se o app já está instalado
  const checkIfInstalled = (): boolean => {
    // Desktop e Android: verifica display-mode
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // iOS: verifica navigator.standalone
    if ('standalone' in window.navigator && (window.navigator as any).standalone === true) {
      return true;
    }

    return false;
  };

  // Handler para o evento beforeinstallprompt
  const handleBeforeInstallPrompt = (e: Event) => {
    // Previne o mini-infobar padrão do Chrome
    e.preventDefault();

    // Armazena o evento para usar depois
    deferredPrompt.value = e as BeforeInstallPromptEvent;
    canInstall.value = true;

    console.log('📱 PWA: beforeinstallprompt event captured');
  };

  // Handler para quando o app é instalado
  const handleAppInstalled = () => {
    console.log('📱 PWA: App installed successfully');
    deferredPrompt.value = null;
    canInstall.value = false;
    isInstalled.value = true;
  };

  // Mostra o prompt de instalação (Android/Desktop)
  const showInstallPrompt = async (): Promise<boolean> => {
    if (!deferredPrompt.value) {
      console.warn('📱 PWA: No deferred prompt available');
      return false;
    }

    // Mostra o prompt
    await deferredPrompt.value.prompt();

    // Espera a escolha do usuário
    const choiceResult = await deferredPrompt.value.userChoice;

    console.log(`📱 PWA: User choice: ${choiceResult.outcome}`);

    if (choiceResult.outcome === 'accepted') {
      deferredPrompt.value = null;
      canInstall.value = false;
      return true;
    }

    return false;
  };

  // Verifica se deve mostrar o botão de instalação
  const shouldShowInstallButton = (): boolean => {
    // Se já está instalado, não mostrar
    if (isInstalled.value) {
      return false;
    }

    // Se é iOS, sempre mostrar (porque não há beforeinstallprompt)
    if (platform.value === 'ios') {
      return true;
    }

    // Para outras plataformas, mostrar apenas se canInstall for true
    return canInstall.value;
  };

  // Setup dos event listeners
  onMounted(() => {
    platform.value = detectPlatform();
    isInstalled.value = checkIfInstalled();

    console.log(`📱 PWA: Platform detected: ${platform.value}`);
    console.log(`📱 PWA: Is installed: ${isInstalled.value}`);

    // Adiciona listeners (não funciona no iOS)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
  });

  // Cleanup dos event listeners
  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', handleAppInstalled);
  });

  return {
    canInstall,
    isInstalled,
    platform,
    deferredPrompt,
    showInstallPrompt,
    shouldShowInstallButton
  };
}
